import { getPinnedComment, getPinSnippet } from './autoLayout';
import { getActivityPageHref, isGenericYouTubeLabel, parseYouTubeVideoId } from './youtubeUrl';
import { readArrowLabel } from './arrowTerminalLabels';
import { resolveDisplayTitle } from './pinTitle';
import { formatAiCostExtras, normalizeAiCost } from './aiCostFormat';
import { resolveBlockerDoneFill } from './blockerDoneFill';
import { extractTextShapeContent } from './canvasTextContent';
import {
  collectTextAboutLinks,
  findLinkForText,
  findLinksForTarget,
} from './textAboutObject';

export { resolveBlockerDoneFill } from './blockerDoneFill';

function trimText(value, max = 400) {
  const text = String(value || '').trim();
  if (!text) return '';
  if (text.length <= max) return text;
  return `${text.slice(0, max - 1)}…`;
}

function extractShapeText(shape) {
  return extractTextShapeContent(shape);
}

function pinIntentPreview(activity) {
  if (!activity) return null;
  const comment = getPinnedComment(activity);
  const highlight = comment?.highlightData || activity?.action?.highlightData || {};
  const intentSummary = trimText(highlight?.intentSummary, 400);
  if (intentSummary) {
    return { source: 'intentSummary', text: intentSummary };
  }
  const snippet = getPinSnippet(activity);
  const pageHref = getActivityPageHref(activity);
  const fallback = parseYouTubeVideoId(pageHref) && isGenericYouTubeLabel(snippet)
    ? ''
    : trimText(snippet, 400);
  if (fallback) {
    return { source: 'snippet/fallback', text: fallback };
  }
  return null;
}

function labelForBoundTarget(editor, shapeId, activityById) {
  if (!editor || !shapeId) return '';
  const shape = editor.getShape(shapeId);
  if (!shape) return String(shapeId);
  if (shape.type === 'pin-card' && shape.meta?.activityId) {
    const activity = activityById?.(String(shape.meta.activityId));
    return resolveDisplayTitle(
      activity?.content?.[0]?.title,
      activity?.content?.[0]?.urls?.[0]?.href,
      { snippet: activity?.snippet, fallback: 'Pin' }
    );
  }
  if (shape.type === 'frame') {
    return String(shape.props?.name || 'Frame').trim() || 'Frame';
  }
  if (shape.type === 'text' || shape.type === 'note') {
    return trimText(extractShapeText(shape), 40) || shape.type;
  }
  if (shape.type === 'suggest-pin' || shape.type === 'conversation-pin') {
    return String(shape.props?.title || shape.type).trim();
  }
  return shape.type || String(shapeId);
}

function readAiCostFromShape(shape, activity = null) {
  const fromMeta = normalizeAiCost({
    estimatedCostUsd: shape?.meta?.aiCostUsd,
    totalTokenCount: shape?.meta?.aiTokens,
    parts: shape?.meta?.aiCostParts,
  });
  if (fromMeta) return fromMeta;
  const comment = getPinnedComment(activity);
  const highlight = comment?.highlightData || activity?.action?.highlightData || {};
  return normalizeAiCost({
    estimatedCostUsd: highlight.aiCostUsd,
    totalTokenCount: highlight.aiTokens,
    parts: highlight.aiCostParts,
  });
}

function withAiCost(preview, aiCost) {
  if (!preview) return null;
  const extras = formatAiCostExtras(aiCost);
  if (!extras.length) return preview;
  return {
    ...preview,
    aiCost,
    extras: [
      ...(preview.extras || []),
      ...extras,
    ],
  };
}

function withBlockerDone(preview, blockerDone) {
  if (!preview || !blockerDone?.doneKey) return preview;
  const kindLabel = blockerDone.kind === 'validation' ? 'validation' : 'blocker';
  return {
    ...preview,
    blockerDone,
    extras: [
      { label: `done ${kindLabel}`, text: blockerDone.doneKey },
      ...(preview.extras || []),
    ],
  };
}

function pushEpistemicExtra(extras, value) {
  const epistemic = String(value || '').trim();
  if (epistemic) extras.push({ label: 'epistemic', text: epistemic });
}

function pushNearbyNotes(extras, editor, aboutLinks, shapeId) {
  const captions = findLinksForTarget(aboutLinks, shapeId);
  for (const link of captions.slice(0, 3)) {
    const captionShape = editor?.getShape?.(link.textId);
    const caption = trimText(extractShapeText(captionShape), 160);
    if (caption) extras.push({ label: 'nearby note', text: caption });
  }
}

function pinHeaderMeta({ title, icon }) {
  return {
    title: trimText(title, 120),
    icon: icon || null,
  };
}

/**
 * Preview of the meaning AI would use for a hovered canvas shape.
 * Mirrors server serializeCanvasAnnotations / pin intentSummary preference.
 *
 * @returns {{
 *   kind: string,
 *   source: string,
 *   text: string,
 *   title?: string,
 *   icon?: { kind: 'favicon' | 'ai', host?: string } | null,
 *   aiCost?: object,
 *   blockerDone?: { doneKey: string, kind: string, source: string },
 *   extras: Array<{ label: string, text: string }>
 * } | null}
 */
export function readShapeMeaningPreview(editor, shape, activityById, {
  doneFills = null,
} = {}) {
  if (!shape) return null;

  const extras = [];
  const type = shape.type;
  const aboutLinks = editor ? collectTextAboutLinks(editor) : [];
  const pinActivityId = type === 'pin-card' && shape.meta?.activityId != null
    ? String(shape.meta.activityId)
    : '';
  const pinActivity = pinActivityId ? activityById?.(pinActivityId) : null;
  const aiCost = readAiCostFromShape(shape, pinActivity);
  const blockerDone = resolveBlockerDoneFill(shape, {
    activityId: pinActivityId,
    doneFills,
  });

  const finish = (preview) => withBlockerDone(withAiCost(preview, aiCost), blockerDone);

  if (type === 'pin-card') {
    const activity = pinActivity;
    const preview = pinIntentPreview(activity);
    const comment = getPinnedComment(activity);
    const highlight = comment?.highlightData || activity?.action?.highlightData || {};
    const selectionText = trimText(
      highlight.selectionText || highlight.ocrText || '',
      400
    );
    const selectionSource = String(highlight.selectionTextSource || '').trim();
    const epistemic = String(activity?.epistemicType || '').trim();
    const content = activity?.content?.[0];
    const url = content?.urls?.[0];
    const faviconHost = String(url?.host || url?.hostname || '').trim();
    const header = pinHeaderMeta({
      title: resolveDisplayTitle(content?.title, url?.href, {
        snippet: activity?.snippet,
        fallback: 'Pin',
      }),
      icon: faviconHost ? { kind: 'favicon', host: faviconHost } : { kind: 'favicon' },
    });
    if (selectionText) {
      extras.push({
        label: selectionSource ? `selectionText (${selectionSource})` : 'selectionText',
        text: selectionText,
      });
    }
    if (epistemic) extras.push({ label: 'epistemic', text: epistemic });
    pushNearbyNotes(extras, editor, aboutLinks, shape.id);
    if (!preview) {
      return finish({
        kind: type,
        source: selectionText ? 'selectionText' : 'none',
        text: selectionText || '(no intentSummary yet)',
        ...header,
        extras: selectionText
          ? extras.filter(e => !String(e.label).startsWith('selectionText'))
          : extras,
      });
    }
    return finish(
      { kind: type, source: preview.source, text: preview.text, ...header, extras }
    );
  }

  if (type === 'suggest-pin') {
    const displayTitle = resolveDisplayTitle(shape.props?.title, shape.props?.url, {
      snippet: shape.props?.snippet,
      fallback: 'Suggested',
    });
    const title = trimText(displayTitle, 120);
    const snippet = trimText(shape.props?.snippet, 200);
    const reason = trimText(shape.props?.reason, 160);
    if (reason) extras.push({ label: 'reason', text: reason });
    if (snippet && snippet !== title) extras.push({ label: 'snippet', text: snippet });
    pushEpistemicExtra(extras, shape.meta?.epistemicType);
    pushNearbyNotes(extras, editor, aboutLinks, shape.id);
    const meaningText = reason || (snippet && snippet !== title ? snippet : '') || title
      || '(empty suggest-pin)';
    return finish({
      kind: type,
      source: 'props',
      text: meaningText,
      ...pinHeaderMeta({ title, icon: { kind: 'ai' } }),
      extras,
    });
  }

  if (type === 'conversation-pin') {
    const title = trimText(shape.props?.title, 120);
    const snippet = trimText(shape.props?.snippet, 200);
    if (snippet && snippet !== title) extras.push({ label: 'snippet', text: snippet });
    pushEpistemicExtra(extras, shape.meta?.epistemicType);
    pushNearbyNotes(extras, editor, aboutLinks, shape.id);
    const meaningText = (snippet && snippet !== title ? snippet : '') || title
      || '(empty conversation-pin)';
    return finish({
      kind: type,
      source: 'props',
      text: meaningText,
      ...pinHeaderMeta({
        title: title || 'Chat pin',
        icon: { kind: 'ai' },
      }),
      extras,
    });
  }

  if (type === 'text' || type === 'note') {
    const intentSummary = trimText(shape.meta?.intentSummary, 300);
    const text = trimText(extractShapeText(shape), 300);
    if (intentSummary && text && text !== intentSummary) {
      extras.push({ label: 'text', text });
    }
    const pinCase = String(shape.meta?.pinCase || '').trim();
    if (pinCase) extras.push({ label: 'pinCase', text: pinCase });
    const about = findLinkForText(aboutLinks, shape.id);
    if (about?.targetId) {
      extras.push({
        label: 'about',
        text: labelForBoundTarget(editor, about.targetId, activityById) || about.aboutId,
      });
    }
    pushEpistemicExtra(extras, shape.meta?.epistemicType);
    return finish({
      kind: type,
      source: intentSummary ? 'intentSummary' : 'text',
      text: intentSummary || text || '(empty)',
      extras,
    });
  }

  if (type === 'image' || type === 'bookmark' || type === 'embed') {
    const intentSummary = trimText(shape.meta?.intentSummary, 300);
    const ocrText = trimText(shape.meta?.ocrText, 300);
    const url = trimText(shape.props?.url || shape.props?.src, 160);
    if (ocrText) extras.push({ label: 'ocrText', text: ocrText });
    if (url) extras.push({ label: 'url', text: url });
    pushEpistemicExtra(extras, shape.meta?.epistemicType);
    pushNearbyNotes(extras, editor, aboutLinks, shape.id);
    return finish({
      kind: type,
      source: intentSummary ? 'intentSummary' : (ocrText ? 'ocrText' : 'url'),
      text: intentSummary || ocrText || url || '(no meaning meta)',
      extras,
    });
  }

  if (type === 'arrow') {
    const label = readArrowLabel(shape);
    const bindings = editor?.getBindingsFromShape?.(shape.id, 'arrow') || [];
    let startTarget = '';
    let endTarget = '';
    for (const binding of bindings) {
      const terminal = binding?.props?.terminal;
      const target = labelForBoundTarget(editor, binding?.toId, activityById);
      if (terminal === 'start') startTarget = target;
      if (terminal === 'end') endTarget = target;
    }
    if (label) extras.push({ label: 'label', text: label });
    if (startTarget) extras.push({ label: 'from', text: startTarget });
    if (endTarget) extras.push({ label: 'to', text: endTarget });
    const text =
      label
      || [startTarget && `from ${startTarget}`, endTarget && `to ${endTarget}`]
        .filter(Boolean)
        .join(' ')
      || '(unlabeled connect line)';
    return finish({
      kind: type,
      source: label ? 'labels' : 'bindings',
      text,
      extras,
    });
  }

  if (type === 'frame' || type === 'geo') {
    const label = trimText(shape.props?.name || shape.props?.text || shape.props?.geo, 120);
    return finish({
      kind: type,
      source: 'label',
      text: label || `(unnamed ${type})`,
      extras,
    });
  }

  if (type === 'draw' || type === 'highlight' || type === 'line') {
    return finish({
      kind: type,
      source: 'bounds',
      text: `(${type} — no text meaning)`,
      extras,
    });
  }

  return finish({
    kind: type || 'shape',
    source: 'none',
    text: `(${type || 'shape'})`,
    extras,
  });
}
